import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import PropTypes from "prop-types";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ChatLogo from "../../Asssets/Images/chat_logo.png";

const markdownStyles = {
  "& p": {
    margin: "4px 0",
    "&:first-of-type": { marginTop: 0 },
    "&:last-of-type": { marginBottom: 0 },
  },
  "& ul, & ol": {
    margin: "4px 0",
    paddingLeft: "20px",
  },
  "& li": {
    margin: "2px 0",
    fontSize: "0.875rem",
    lineHeight: 1.5,
  },
  "& strong": {
    fontWeight: 600,
  },
  "& h4, & h5, & h6": {
    margin: "8px 0 4px 0",
    fontSize: "0.875rem",
    fontWeight: 600,
  },
  "& h3": {
    margin: "8px 0 4px 0",
    fontSize: "0.925rem",
    fontWeight: 600,
  },
  "& hr": {
    border: "none",
    borderTop: "1px solid rgba(0,0,0,0.1)",
    margin: "8px 0",
  },
  "& table": {
    borderCollapse: "collapse",
    width: "100%",
    margin: "6px 0",
    fontSize: "0.8rem",
  },
  "& thead th": {
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  "& th, & td": {
    border: "1px solid rgba(0,0,0,0.15)",
    padding: "4px 8px",
    textAlign: "left",
    verticalAlign: "top",
  },
  "& th": {
    fontWeight: 600,
    backgroundColor: "rgba(0,0,0,0.04)",
  },
  "& code": {
    backgroundColor: "rgba(0,0,0,0.06)",
    padding: "1px 4px",
    borderRadius: "3px",
    fontSize: "0.8rem",
  },
  "& blockquote": {
    borderLeft: "3px solid rgba(0,0,0,0.2)",
    margin: "6px 0",
    paddingLeft: "10px",
    color: "rgba(0,0,0,0.65)",
  },
};

const markdownComponents = {
  table: ({ children, ...props }) => (
    <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
      <table {...props}>{children}</table>
    </Box>
  ),
};

const ChatMessage = ({ message }) => {
  const isUser = message.role === "user";
  const isError = message.isError;
  let backgroundColor = "#f0f4f8";
  let textColor = "#1a1a1a";
  if (isUser) {
    backgroundColor = "#1976d2";
    textColor = "#fff";
  } else if (isError) {
    backgroundColor = "#fff3f3";
    textColor = "#d32f2f";
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        mb: 1.5,
        gap: 1,
        alignItems: "flex-start",
      }}
    >
      {!isUser && (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            backgroundColor: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            mt: 0.5,
          }}
        >
          <img
            src={ChatLogo}
            alt="HR Buddy"
            style={{ width: 32, height: 32, objectFit: "contain", background: "transparent" }}
          />
        </Box>
      )}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          maxWidth: "80%",
          borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
          backgroundColor,
          color: textColor,
          wordBreak: "break-word",
        }}
      >
        {isUser ? (
          <Typography
            variant="body2"
            sx={{ fontSize: "0.875rem", lineHeight: 1.5 }}
          >
            {message.content}
          </Typography>
        ) : (
          <Box
            sx={{
              fontSize: "0.875rem",
              lineHeight: 1.5,
              ...markdownStyles,
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {message.content}
            </ReactMarkdown>
          </Box>
        )}
        {message.timestamp && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.5,
              opacity: 0.7,
              fontSize: "0.7rem",
              textAlign: isUser ? "right" : "left",
              color: isUser ? "rgba(255,255,255,0.7)" : "text.secondary",
            }}
          >
            {new Date(message.timestamp).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </Typography>
        )}
      </Paper>
      {isUser && (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            backgroundColor: "#e0e0e0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            mt: 0.5,
          }}
        >
          <PersonIcon sx={{ color: "#666", fontSize: 18 }} />
        </Box>
      )}
    </Box>
  );
};

ChatMessage.propTypes = {
  message: PropTypes.shape({
    role: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    isError: PropTypes.bool,
    timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
  }).isRequired,
};

export default ChatMessage;
